import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Info, Loader2, Send, X, XCircle, type LucideIcon } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastReplyConfig {
    onSend: (text: string) => Promise<void> | void;
    placeholder?: string;
}

export interface ToastCardProps {
    type: ToastType;
    title: string;
    message: string;
    onDismiss: () => void;
    onClick?: () => void;
    reply?: ToastReplyConfig;
    avatarUrl?: string;
    onReplyOpen?: () => void;
}

const typeStyles: Record<ToastType, { icon: LucideIcon; tone: string; accent: string }> = {
    success: { icon: CheckCircle2, tone: "text-status_success", accent: "border-l-status_success" },
    error: { icon: XCircle, tone: "text-status_danger", accent: "border-l-status_danger" },
    warning: { icon: AlertTriangle, tone: "text-status_warning", accent: "border-l-status_warning" },
    info: { icon: Info, tone: "text-status_info", accent: "border-l-status_info" },
};

const REPLY_SENT_VISIBLE_MS = 900;

const initialsOf = (name: string) =>
    name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("");

export function ToastCard({ type, title, message, onDismiss, onClick, reply, avatarUrl, onReplyOpen }: ToastCardProps) {
    const { t } = useTranslation("common");
    const [replyOpen, setReplyOpen] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    // O sonner recria o render do toast; ler pela ref evita reiniciar o timer a cada render.
    const dismissRef = useRef(onDismiss);
    dismissRef.current = onDismiss;

    const { icon: Icon, tone, accent } = typeStyles[type];

    useEffect(() => {
        if (replyOpen) inputRef.current?.focus();
    }, [replyOpen]);

    useEffect(() => {
        if (!sent) return;
        const timer = setTimeout(() => dismissRef.current(), REPLY_SENT_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, [sent]);

    const openReply = () => {
        setReplyOpen(true);
        onReplyOpen?.();
    };

    const handleSend = async () => {
        const text = replyText.trim();
        if (!text || !reply || sending) return;
        setSending(true);
        try {
            await reply.onSend(text);
            setReplyText("");
            setSent(true);
        } catch {
            setSending(false);
        }
    };

    return (
        <div
            data-toast-type={type}
            onClick={onClick ? () => { onClick(); onDismiss(); } : undefined}
            className={cn(
                "flex w-full flex-col gap-2 rounded-xl border border-l-4 border-slate-200 bg-white p-3 text-left shadow-lg dark:border-white/10 dark:bg-night_surface md:p-4",
                accent,
                onClick && "cursor-pointer transition-colors hover:bg-slate-50 active:bg-slate-100 dark:hover:bg-night_input"
            )}
        >
            <div className="flex items-start gap-3">
                {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                ) : reply ? (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600 dark:bg-white/10 dark:text-white/70">
                        {initialsOf(title)}
                    </span>
                ) : (
                    <Icon aria-hidden="true" className={cn("mt-0.5 h-5 w-5 shrink-0", tone)} />
                )}

                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold leading-tight text-slate-900 dark:text-white">{title}</p>
                    <p className="mt-0.5 line-clamp-3 text-[13px] leading-snug text-slate-600 dark:text-white/70 md:line-clamp-2 md:text-xs">
                        {message}
                    </p>
                    {reply && !replyOpen && !sent && (
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                openReply();
                            }}
                            className="-ml-1 mt-1 inline-flex h-9 items-center rounded-md px-1 text-[13px] font-semibold text-dark_green hover:underline dark:text-emerald-400 md:h-auto md:text-xs"
                        >
                            {t("nav.notifications.reply", "Responder")}
                        </button>
                    )}
                </div>

                <button
                    type="button"
                    aria-label={t("general.close")}
                    onClick={(event) => {
                        event.stopPropagation();
                        onDismiss();
                    }}
                    className="-mr-1 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white md:h-7 md:w-7"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>

            {reply && replyOpen && !sent && (
                <form
                    onClick={(event) => event.stopPropagation()}
                    onSubmit={(event) => {
                        event.preventDefault();
                        void handleSend();
                    }}
                    className="flex items-center gap-2 md:pl-12"
                >
                    <input
                        ref={inputRef}
                        value={replyText}
                        onChange={(event) => setReplyText(event.target.value)}
                        disabled={sending}
                        placeholder={reply.placeholder || t("nav.notifications.replyPlaceholder", "Responder...")}
                        className="h-10 min-w-0 flex-1 rounded-lg bg-slate-100 px-3 text-slate-800 outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500/40 disabled:opacity-60 dark:bg-white/10 dark:text-white dark:placeholder:text-white/40 md:h-8 md:text-xs"
                    />
                    <button
                        type="submit"
                        disabled={sending || !replyText.trim()}
                        aria-label={t("nav.notifications.send", "Enviar")}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-dark_green text-white transition-colors hover:bg-dark_green-700 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-emerald-600 dark:hover:bg-emerald-500 md:h-8 md:w-8"
                    >
                        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                </form>
            )}

            {sent && (
                <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 md:pl-12">
                    <CheckCircle2 size={14} /> {t("nav.notifications.replySent", "Resposta enviada")}
                </p>
            )}
        </div>
    );
}
